import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../../hooks/useAuth";
import { useAuthStore } from "../../store/auth";
import { UserInfo } from "../../contexts/UserContext";
import { loginRedirectURL } from "../../hooks/useLogout";

type AppRole = "SUPER_ADMIN" | "STAFF_USER" | "BASIC_USER";

/** Keys from the useAuth hook that represent individual permissions. */
type PermissionKey = "canCreate" | "canUpdate" | "canDelete" | "canManageRestrictions" | "canManageUsers";

interface IProtectedRouteProps {
  children: ReactNode;
  /** Legacy role-based guard — kept for backwards compatibility. */
  allowedRoles?: AppRole[];
  /**
   * Permission-based guard. The user must have at least one of the listed
   * permissions (OR logic). Checked after authentication and allowedRoles.
   */
  requiredPermissions?: PermissionKey[];
}

const getUserAppRole = (user: UserInfo): AppRole => {
  if (user?.is_superuser) return "SUPER_ADMIN";
  if (user?.is_staff) return "STAFF_USER";
  return "BASIC_USER";
};

export const ProtectedRoute = ({ children, allowedRoles, requiredPermissions }: IProtectedRouteProps) => {
  const { isAuthenticated, canCreate, canUpdate, canDelete, canManageRestrictions, canManageUsers } = useAuth();
  const { user } = useAuthStore((state) => state);
  const location = useLocation();

  if (!isAuthenticated || !user) {
    const redirectUrl = loginRedirectURL();
    return <Navigate to={redirectUrl} replace state={{ from: location }} />;
  }

  if (allowedRoles) {
    const currentUserRole = getUserAppRole(user as UserInfo);
    if (!allowedRoles.includes(currentUserRole)) {
      return <Navigate to='/unauthorized' replace />;
    }
  }

  if (requiredPermissions) {
    const permMap: Record<PermissionKey, boolean> = {
      canCreate,
      canUpdate,
      canDelete,
      canManageRestrictions,
      canManageUsers
    };
    const hasPermission = requiredPermissions.some((p) => permMap[p]);
    if (!hasPermission) {
      return <Navigate to='/unauthorized' replace />;
    }
  }

  return children;
};
